import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Fade, InputAdornment,
  MenuItem, Paper, Snackbar, Stack, TextField, Typography
} from "@mui/material";
import {
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import * as XLSX from "xlsx";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from "../../components/AppHeader";
import { fetchUnpaidAbsenceReport } from "../../services/webReportsService";

const toDateKey = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const Metric = ({ label, value, accent = "#334155" }) => (
  <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 3, minWidth: 180, flex: 1 }}>
    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>{label}</Typography>
    <Typography variant="h6" sx={{ color: accent, fontWeight: 900, mt: 0.25 }}>{value}</Typography>
  </Paper>
);

export default function UnpaidAbsenceReport({ setIsLoggedIn }) {
  const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("APPROVED");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const now = new Date();
    setDateFrom(toDateKey(new Date(now.getFullYear(), now.getMonth(), 1)));
    setDateTo(toDateKey(now));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(employeeSearch.trim()), 350);
    return () => clearTimeout(timer);
  }, [employeeSearch]);

  const showMessage = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const loadReport = useCallback(async () => {
    if (!tenant || !dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const result = await fetchUnpaidAbsenceReport({
        date_from: dateFrom,
        date_to: dateTo,
        status,
        employee_search: debouncedSearch || undefined,
      });
      setRows(Array.isArray(result?.rows) ? result.rows : []);
      setMetrics(result?.metrics || {});
    } catch (error) {
      setRows([]);
      setMetrics({});
      showMessage("error", error?.message || "Failed to load unpaid absence report.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, debouncedSearch, showMessage, status, tenant]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const exportExcel = () => {
    const exportRows = rows.map(row => ({
      "Attendance Date": row.attendance_date,
      "Employee ID": row.employee_id || row.staff_code,
      "Staff Name": row.full_name,
      "Department": row.department,
      "Branch": row.branch_name,
      "Absence Portion": row.absence_portion,
      "Equivalent Days": Number(row.equivalent_days || 0),
      "Scheduled Minutes": Number(row.scheduled_minutes || 0),
      "Unpaid Minutes": Number(row.unpaid_minutes || 0),
      "Reason": row.reason,
      "Resolution Status": row.status,
      "Approved By": row.approved_by_name,
      "Payroll Period": row.payroll_period,
      "Payroll Status": row.payroll_status,
      "Salary Deduction": Number(row.salary_deduction || 0),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Unpaid Absences");
    XLSX.writeFile(workbook, `Unpaid_Absence_Report_${dateFrom}_to_${dateTo}.xlsx`);
  };

  const columnDefs = useMemo(() => [
    { headerName: "Date", field: "attendance_date", width: 125 },
    { headerName: "Employee ID", field: "employee_id", width: 145, valueGetter: ({ data }) => data?.employee_id || data?.staff_code || "-" },
    { headerName: "Staff Name", field: "full_name", minWidth: 180, flex: 1 },
    { headerName: "Department", field: "department", minWidth: 160 },
    { headerName: "Branch", field: "branch_name", minWidth: 150 },
    { headerName: "Portion", field: "absence_portion", width: 125 },
    { headerName: "Unpaid Days", field: "equivalent_days", width: 125, valueFormatter: ({ value }) => Number(value || 0).toFixed(2) },
    { headerName: "Unpaid Hours", field: "unpaid_minutes", width: 125, valueFormatter: ({ value }) => (Number(value || 0) / 60).toFixed(2) },
    { headerName: "Reason", field: "reason", minWidth: 220, flex: 1 },
    { headerName: "Status", field: "status", width: 125, cellRenderer: ({ value }) => <Chip size="small" label={value || "-"} color={value === "APPROVED" ? "success" : "warning"} variant="outlined" sx={{ fontWeight: 700 }} /> },
    { headerName: "Approved By", field: "approved_by_name", minWidth: 150 },
    { headerName: "Payroll Period", field: "payroll_period", minWidth: 155 },
    { headerName: "Payroll Status", field: "payroll_status", width: 140 },
    { headerName: "Salary Deduction", field: "salary_deduction", width: 155, valueFormatter: ({ value }) => Number(value || 0).toLocaleString() },
  ], []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />
      <Fade in timeout={400}>
        <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2.25 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, color: "#0f172a" }}>Unpaid Absence Report</Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>Attendance resolutions that reduce salary, kept separate from employee leave requests.</Typography>
            </Box>
            <Stack direction="row" gap={1} alignItems="center">
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadReport} disabled={loading}>Refresh</Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={loading || rows.length === 0}>Export Excel</Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <Metric label="Resolution Records" value={Number(metrics.total_records || 0).toLocaleString()} />
            <Metric label="Affected Employees" value={Number(metrics.affected_employees || 0).toLocaleString()} accent="#7c3aed" />
            <Metric label="Unpaid Days" value={Number(metrics.unpaid_days || 0).toFixed(2)} accent="#dc2626" />
            <Metric label="Payroll Deduction" value={`${Number(metrics.salary_deduction || 0).toLocaleString()} MMK`} accent="#b91c1c" />
          </Stack>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "none" }}>
            <Stack direction={{ xs: "column", lg: "row" }} gap={1.5}>
              <TextField label="Date From" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
              <TextField label="Date To" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
              <TextField select label="Resolution Status" value={status} onChange={e => setStatus(e.target.value)} size="small" sx={{ minWidth: 190 }}>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="ALL">All statuses</MenuItem>
              </TextField>
              <TextField label="Employee ID / Name" value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} size="small" sx={{ flex: 1, minWidth: 260 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "none" }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 800 }}>{loading ? "Loading..." : `${rows.length.toLocaleString()} record${rows.length === 1 ? "" : "s"}`}</Typography>
              {loading && <CircularProgress size={20} />}
            </Stack>
            <Box className="ag-theme-quartz" sx={{ height: "calc(100vh - 395px)", minHeight: 470, width: "100%", "& .ag-header-cell-label": { fontWeight: 800 } }}>
              <AgGridReact rowData={rows} columnDefs={columnDefs} defaultColDef={{ sortable: true, filter: true, resizable: true }} rowHeight={48} headerHeight={48} pagination paginationPageSize={25} overlayNoRowsTemplate="<span style='color:#64748b'>No unpaid absence records found</span>" />
            </Box>
          </Paper>
        </Box>
      </Fade>
      <Snackbar open={snackbar.open} autoHideDuration={4500} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
